import {Component, Input, OnInit} from '@angular/core';
import {AbstractControl, FormBuilder, FormGroup, ValidatorFn, Validators} from '@angular/forms';
import {SelectItem} from 'primeng/api';
import {Producto} from '../../../_models/producto';
import {ProductosCategorias} from '../../../_models/productos-categorias';
import {ListaService} from '../../../_services/lista.service';
import {NavService} from '../../../_services/_nav.service';
import {ProductosCategoriasService} from '../../../_services/productos-categorias.service';

/** Valida que montoMinimo no sea mayor que montoMaximo */
const validarMontos: ValidatorFn = (group: AbstractControl) => {
  const min = group.get('montoMinimo')?.value;
  const max = group.get('montoMaximo')?.value;
  if (min !== null && max !== null && min !== '' && max !== '') {
    const minNum = typeof min === 'number' ? min : parsearMonedaRaw(String(min));
    const maxNum = typeof max === 'number' ? max : parsearMonedaRaw(String(max));
    if (!isNaN(minNum) && !isNaN(maxNum) && minNum > maxNum) {
      return {montoMinMayorMax: true};
    }
  }
  return null;
};

/** Valida que plazoMinimo no sea mayor que plazoMaximo */
const validarPlazos: ValidatorFn = (group: AbstractControl) => {
  const min = Number(group.get('plazoMinimo')?.value);
  const max = Number(group.get('plazoMaximo')?.value);
  if (!isNaN(min) && !isNaN(max) && min > max) {
    return {plazoMinMayorMax: true};
  }
  return null;
};

/** Elimina el formato de moneda colombiana y retorna el número */
function parsearMonedaRaw(valor: string): number {
  if (!valor) {
    return NaN;
  }
  return Number(String(valor).replace(/\$/g, '').replace(/\./g, '').replace(/,/g, '.').trim());
}

/** Valida que la parte entera no supere 9 cifras y la decimal 7 cifras */
const validarLongitudMonto: ValidatorFn = (control: AbstractControl) => {
  if (!control.value) {
    return null;
  }
  const raw = parsearMonedaRaw(String(control.value));
  if (isNaN(raw)) {
    return null;
  }
  const partes = String(Math.abs(raw)).split('.');
  if (partes[0].length > 9) {
    return {montoMaxEnteros: true};
  }
  if (partes[1] && partes[1].length > 7) {
    return {montoMaxDecimales: true};
  }
  return null;
};

@Component({
  selector: 'app-producto-categorias',
  templateUrl: 'producto-categorias.component.html',
  styleUrls: ['./producto-categorias.component.scss']
})
export class ProductoCategoriasComponent implements OnInit {

  @Input() producto: Producto;
  @Input() detalle = false;

  carInformacionInicial = true;
  lisProductosCategorias: ProductosCategorias[] = [];
  columnas: any[];
  search = '';

  mostrarFormulario = false;
  editando = false;
  registroEditando: ProductosCategorias | null = null;
  guardando = false;
  submitted = false;
  formulario: FormGroup;

  lisCategoriasMetasAhorro: SelectItem[] = [];

  constructor(
    private productosCategoriasService: ProductosCategoriasService,
    private lisServicio: ListaService,
    private navServicio: NavService,
    private fb: FormBuilder
  ) {
  }

  ngOnInit(): void {
    this.columnas = [
      {field: 'nombreCategoriasMetasAhorro', header: this.navServicio.buscarMensaje('PRODUCTOCATEGORIAS.LBL_CATEGORIA'), sort: true},
      {field: 'montoMinimo', header: this.navServicio.buscarMensaje('PRODUCTOCATEGORIAS.LBL_MONTOMINIMO'), sort: true},
      {field: 'montoMaximo', header: this.navServicio.buscarMensaje('PRODUCTOCATEGORIAS.LBL_MONTOMAXIMO'), sort: true},
      {field: 'plazoMinimo', header: this.navServicio.buscarMensaje('PRODUCTOCATEGORIAS.LBL_PLAZOMINIMO'), sort: true},
      {field: 'plazoMaximo', header: this.navServicio.buscarMensaje('PRODUCTOCATEGORIAS.LBL_PLAZOMAXIMO'), sort: true},
      {field: 'plazoObligatorio', header: this.navServicio.buscarMensaje('PRODUCTOCATEGORIAS.LBL_PLAZOOBLIGATORIO'), sort: false},
      {field: 'montoObligatorio', header: this.navServicio.buscarMensaje('PRODUCTOCATEGORIAS.LBL_MONTOOBLIGATORIO'), sort: false},
      {field: 'indicadorHabilitado', header: this.navServicio.buscarMensaje('PRODUCTOCATEGORIAS.LBL_ESTADO'), sort: true},
      {field: 'det', header: this.navServicio.buscarMensaje('LBL_ACCIONES'), sort: false}
    ];

    this.lisServicio.getMasterDetails('ListasCategoriasMetasAhorro').subscribe(items => {
      this.lisCategoriasMetasAhorro = items.map(i => ({label: i.nombre, value: i.idLista}));
    });

    this.cargarFormulario();
    this.cargarDatos();
  }

  cargarFormulario(): void {
    this.formulario = this.fb.group({
      idCategoriaMetaAhorro: [null, [Validators.required]],
      montoMinimo: [null, [Validators.required, validarLongitudMonto]],
      montoMaximo: [null, [Validators.required, validarLongitudMonto]],
      plazoMinimo: [null, [Validators.required, Validators.min(1)]],
      plazoMaximo: [null, [Validators.required, Validators.max(360)]],
      plazoObligatorio: [false],
      montoObligatorio: [false],
      indicadorHabilitado: [true]
    }, {validators: [validarMontos, validarPlazos]});
  }

  get f() {
    return this.formulario.controls;
  }

  cargarDatos(): void {
    this.carInformacionInicial = true;
    this.productosCategoriasService.listadoPorProducto(this.producto.idProducto).subscribe(
      categorias => {
        this.lisProductosCategorias = categorias;
        this.carInformacionInicial = false;
      },
      () => {
        this.carInformacionInicial = false;
      }
    );
  }

  /** Abre el formulario de agregar con valores iniciales */
  agregar(): void {
    this.formulario.reset();
    this.f.indicadorHabilitado.setValue(true);
    this.f.plazoObligatorio.setValue(false);
    this.f.montoObligatorio.setValue(false);
    this.f.idCategoriaMetaAhorro.enable();
    this.mostrarFormulario = true;
    this.submitted = false;
    this.editando = false;
    this.registroEditando = null;
  }

  /** Cierra el formulario sin guardar y limpia el estado de edición */
  cancelarEdit(): void {
    this.formulario.reset();
    this.f.idCategoriaMetaAhorro.enable();
    this.mostrarFormulario = false;
    this.submitted = false;
    this.editando = false;
    this.registroEditando = null;
  }

  /** crea uno nuevo o actualiza el existente según el modo */
  onSubmit(): void {
    this.submitted = true;
    if (this.formulario.invalid) {
      return;
    }

    const datos = this.formulario.getRawValue();

    if (!this.editando) {
      const yaExiste = this.lisProductosCategorias.some(
        c => c.idCategoriaMetaAhorro === datos.idCategoriaMetaAhorro
      );
      if (yaExiste) {
        this.f.idCategoriaMetaAhorro.setErrors({categoriaExistente: true});
        return;
      }
    }

    this.guardando = true;

    const registro: ProductosCategorias = this.editando && this.registroEditando
      ? {...this.registroEditando}
      : new ProductosCategorias();

    registro.idProducto = this.producto.idProducto;
    if (!this.editando) {
      registro.idCategoriaMetaAhorro = datos.idCategoriaMetaAhorro;
    }
    registro.montoMinimo = String(parsearMonedaRaw(String(datos.montoMinimo)));
    registro.montoMaximo = String(parsearMonedaRaw(String(datos.montoMaximo)));
    registro.plazoMinimo = String(datos.plazoMinimo);
    registro.plazoMaximo = String(datos.plazoMaximo);
    registro.plazoObligatorio = datos.plazoObligatorio ?? false;
    registro.montoObligatorio = datos.montoObligatorio ?? false;
    registro.indicadorHabilitado = datos.indicadorHabilitado;

    const operacion = this.editando
      ? this.productosCategoriasService.actualizar(registro)
      : this.productosCategoriasService.crear(registro);

    operacion.subscribe(
      () => {
        this.cargarDatos();
        this.cancelarEdit();
        this.guardando = false;
        this.navServicio.setMesage(1);
      },
      () => {
        this.guardando = false;
        this.navServicio.setMesage(3);
      }
    );
  }

  /** Abre el formulario precargado con los datos del registro a editar */
  editar(row: ProductosCategorias): void {
    this.submitted = false;
    this.editando = true;
    this.registroEditando = row;
    this.formulario.patchValue({
      idCategoriaMetaAhorro: row.idCategoriaMetaAhorro,
      montoMinimo: this.formatearMoneda(row.montoMinimo),
      montoMaximo: this.formatearMoneda(row.montoMaximo),
      plazoMinimo: Number(row.plazoMinimo),
      plazoMaximo: Number(row.plazoMaximo),
      plazoObligatorio: row.plazoObligatorio,
      montoObligatorio: row.montoObligatorio,
      indicadorHabilitado: row.indicadorHabilitado
    });
    this.f.idCategoriaMetaAhorro.disable();
    this.mostrarFormulario = true;
  }

  /**
   * Formatea un valor numérico como moneda colombiana para mostrar en tabla
   */
  formatearMoneda(valor: string | number): string {
    if (valor === null || valor === undefined || valor === '') {
      return '';
    }
    const num = Number(valor);
    if (isNaN(num)) {
      return String(valor);
    }
    return '$' + num.toLocaleString('es-CO', {maximumFractionDigits: 7});
  }

  /**
   * Aplica formato de moneda colombiana sobre el campo en el formulario al salir del foco
   */
  formatearCampoMoneda(controlName: string): void {
    const ctrl = this.formulario.get(controlName);
    if (!ctrl || !ctrl.value) {
      return;
    }
    const raw = parsearMonedaRaw(String(ctrl.value));
    if (!isNaN(raw)) {
      ctrl.setValue('$' + raw.toLocaleString('es-CO', {maximumFractionDigits: 7}), {emitEvent: false});
    }
  }

  /** Permite únicamente dígitos (campos de plazo) */
  soloNumericos(event: KeyboardEvent): boolean {
    return /[0-9]/.test(event.key);
  }

  /** Permite dígitos y coma decimal (campos de monto) */
  soloNumericosConDecimal(event: KeyboardEvent): boolean {
    const input = event.target as HTMLInputElement;
    if (event.key === ',') {
      return !input.value.includes(',');
    }
    return /[0-9]/.test(event.key);
  }

  /**
   * Devuelve la etiqueta de estado según el indicador habilitado
   */
  obtenerEstado(indicador: boolean): string {
    return indicador
      ? this.navServicio.buscarMensaje('PRODUCTOCATEGORIAS.LBL_HABILITADO')
      : this.navServicio.buscarMensaje('PRODUCTOCATEGORIAS.LBL_INHABILITADO');
  }
}
